import {
  DropdownButton,
  DropdownButtonItem,
  ListItem,
  ListItemMeta,
  Modal,
  Text,
} from '@salecycle/ui-component-library';
import UserLink from 'core/ui/components/userLink';
import { getActivityState } from 'entities/lifecycle/activityState';
import { RulesetSplitTest } from 'entities/rulesetSplitTest';
import moment from 'moment';
import * as React from 'react';
import { connect } from 'react-redux';
import ReactTimeAgo from 'react-timeago';
import { Actions as RouterActions, Selectors as RouterSelectors } from 'router/';
import { Selectors as CampaignSelectors } from 'sections/campaigns/state/campaigns';
import { Selectors as RulesetSelectors } from 'sections/campaigns/state/rulesets';
import { Actions, Selectors } from 'sections/campaigns/state/rulesetSplitTests';
import { State } from 'store';
import DefaultTemplatePercentageSummary from './defaultTemplatePercentageSummary';
import VariantTemplatePercentageSummary from './variantTemplatePercentageSummary';

interface OwnProps {
  splitTestId: string;
}

interface StateProps extends OwnProps {
  splitTest: RulesetSplitTest;
  campaignName: string;
  rulesetName: string;
  siteId: string;
}

interface DispatchProps {
  push: (url: string) => void;
  save: (id: string, splitTest: RulesetSplitTest, params: {}) => void;
}

type Props = StateProps & DispatchProps;

const isLive = (splitTest: RulesetSplitTest): boolean => getActivityState(splitTest) === 'Live';
const isArchived = (splitTest: RulesetSplitTest): boolean => getActivityState(splitTest) === 'Archived';

const iconPath = (splitTest: RulesetSplitTest): string => {
  const fileNameSuffix = isLive(splitTest) ? 'Green' : 'Inactive';
  return `/images/campaign_types/Marketing-Permissions-${fileNameSuffix}.svg`;
};
const msInDay = 24 * 60 * 60 * 1000;

export const TableRow: React.FC<Props> = props => {
  const [showStopModal, setShowStopModal] = React.useState(false);

  const { splitTest } = props;
  const formatString = 'D MMM YYYY';
  const from = moment(splitTest.startTime);
  const to = moment(splitTest.endTime);
  const days = Math.floor((splitTest.endTime - splitTest.startTime) / msInDay);
  const now = moment();
  const hasntStartedYet = from.isAfter(now);

  const menuItems: any[] = [];
  if (hasntStartedYet && !isArchived(splitTest)) {
    menuItems.push({
      onClick: () => props.push(`/sites/${props.siteId}/campaigns/split_tests/${props.splitTestId}/settings`),
      label: 'Edit Split Test',
    });
  }
  if (isLive(splitTest)) {
    menuItems.push({
      onClick: () => setShowStopModal(true),
      label: 'Stop Split Test',
    });
  } else if (props.splitTest.state !== 'DELETED') {
    menuItems.push({
      onClick: () =>
        props.save(
          props.splitTestId,
          {
            ...props.splitTest,
            state: 'DELETED',
          },
          {
            campaignId: props.splitTest.campaignId,
            rulesetId: props.splitTest.rulesetId,
          },
        ),
      label: 'Delete Split Test',
    });
  }
  if (isArchived(splitTest)) {
    menuItems.push({
      onClick: () =>
        props.save(
          props.splitTestId,
          {
            ...props.splitTest,
            state: 'INACTIVE',
          },
          {
            campaignId: props.splitTest.campaignId,
            rulesetId: props.splitTest.rulesetId,
          },
        ),
      label: 'Restore Split Test',
    });
  }

  const hideModal = () => setShowStopModal(false);

  const stop = () => {
    hideModal();
    props.save(
      props.splitTestId,
      {
        ...splitTest,
        state: 'INACTIVE',
      },
      {
        campaignId: splitTest.campaignId,
        rulesetId: splitTest.rulesetId,
      },
    );
  };

  return (
    <>
      <ListItem
        options={
          <div>
            <DropdownButton variation="clean" icon="ellipsis-v" menuPlacement="right">
              {menuItems.map(x => (
                <DropdownButtonItem key={`DropdownButton-${props.splitTestId}`} onClick={x.onClick}>
                  {x.label}
                </DropdownButtonItem>
              ))}
            </DropdownButton>
          </div>
        }
      >
        <ListItemMeta heading={splitTest.name} description={splitTest.type} avatarImage={iconPath(splitTest)} />
        <div>
          <Text size={14} weight="medium" variation="primary">
            {props.campaignName}
          </Text>
          <br />
          <Text size={14} weight="medium">
            {props.rulesetName}
          </Text>
        </div>
        <DefaultTemplatePercentageSummary rulesetSplitTestId={props.splitTestId} />
        <VariantTemplatePercentageSummary rulesetSplitTestId={props.splitTestId} />
        <div>
          <Text size={14} weight="medium">{`${from.format(formatString)} - ${to.format(formatString)}`}</Text> (
          <Text size={14} weight="medium" variation="secondary">
            {days} days
          </Text>
          )
          {splitTest.state === 'INACTIVE' ? (
            <>
              {' '}
              (
              <Text size={14} weight="medium" variation="secondary">
                Stopped Early
              </Text>
              )
            </>
          ) : null}
        </div>
        <div>
          <UserLink id={splitTest.audit.lastUpdatedBy} /> <ReactTimeAgo date={splitTest.audit.lastUpdatedAt} />
        </div>
      </ListItem>
      <Modal
        variation="clean"
        isOpen={showStopModal}
        heading="Stop Split Test Confirmation"
        okButtonLabel={'Stop'}
        onOkButtonClicked={stop}
        onCancelButtonClicked={hideModal}
      >
        <p>
          You&apos;re about to stop split test <em>{splitTest.name || ''}</em>. Once stopped a ruleset cannot be
          restarted again.
        </p>
      </Modal>
    </>
  );
};

const mapState = (state: State, ownProps: OwnProps): StateProps => {
  const splitTest = Selectors.getUpdatedEntity(state, ownProps.splitTestId);
  const campaign = CampaignSelectors.getEntity(state, splitTest.campaignId);
  const ruleset = RulesetSelectors.getEntity(state, splitTest.rulesetId);

  return {
    ...ownProps,
    splitTest,
    campaignName: (campaign && campaign.name) || '',
    rulesetName: (ruleset && ruleset.name) || '',
    siteId: RouterSelectors.getSiteId(state),
  };
};

const actionCreators: DispatchProps = {
  push: RouterActions.push,
  save: Actions.requestUpdate,
};

export default connect(mapState, actionCreators)(TableRow);
